import { MetaConversation } from 'lib-shared-legacy-types';
import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import * as conversationActions from 'components/conversation/actions/conversation';
import { notificationActions } from 'components/notifications/actions/notification';

import { jabraActions } from './jabra.actions';

const {
  jabraList,
  jabraAttached,
  jabraDetached,

  jabraButtonClicked,
  jabraButtonClickedUnsupported,
  jabraButtonClickedAcceptedCallOfferConversation,
  jabraButtonClickedRejectedCallOfferConversation,
} = jabraActions;

const { notificationAdd, notificationRemoveByType } = notificationActions;

const {
  conversationRespondeToOfferAccepted,
  conversationRespondeToOfferRejected,
  conversationRespondeToTransferOfferAccepted,
  conversationRespondeToTransferOfferRejected,
  conversationClose,
} = conversationActions.actions;

export const jabraSagas = [
  takeLatest(jabraAttached.toString(), jabraAttachedGen),
  takeLatest(jabraDetached.toString(), jabraDetachedGen),
  takeEvery(jabraButtonClicked.toString(), jabraButtonClickedGen),
  takeEvery(
    jabraButtonClickedAcceptedCallOfferConversation.toString(),
    jabraButtonClickedAcceptedCallOfferConversationGen,
  ),
  takeEvery(
    jabraButtonClickedRejectedCallOfferConversation.toString(),
    jabraButtonClickedRejectedCallOfferConversationGen,
  ),
];

function* jabraAttachedGen(action: any) {
  const option = {
    title: 'Jabra device attached',
    text: action.payload?.device?.deviceName
      ? action.payload.device.deviceName
      : 'Jabra device attached',
    showDesktop: false,
    icon: 'headset',
    time: 5000,
    mainAction: {
      navigate: '/dashboard',
    },
  };
  yield put(notificationAdd(option));

  // we refetch the jabra devices.
  yield put(jabraList());
}

function* jabraDetachedGen(action: any) {
  const option = {
    title: 'Jabra device detached',
    text: action.payload?.device?.deviceName
      ? action.payload.device.deviceName
      : 'A Jabra device has been removed',
    showDesktop: false,
    icon: 'headset',
    time: 5000,
    mainAction: {
      navigate: '/dashboard',
    },
  };
  yield put(notificationAdd(option));

  // we refetch the jabra devices.
  yield put(jabraList());
}

function* jabraButtonClickedGen(action: any) {
  if (
    action.payload.button === 'OffHook' &&
    action.payload.buttonValue === false
  ) {
    // 1. This saga here listens to device btn clicked "OffHook" with value "false"
    // 2. If it finds any conversation that is not closed -
    // the current agent is assigned to and is of the type phone/callback closes the conversation(s).

    const getTransferToAgent = (state) =>
      state.transferToAgent.metaConversation;
    const getTransferToAgentState = yield select(getTransferToAgent);
    const getTransferToNumber = (state) =>
      state.transferToNumber.metaConversation;
    const getTransferToNumberState = yield select(getTransferToNumber);

    const getConversations = (state) => state.conversations;
    const conversationsState = yield select(getConversations);
    const activeTransferCSID =
      getTransferToAgentState.conversation.id |
      getTransferToNumberState.conversation.id;

    const foundMetaConversation = conversationsState.metaConversations.find(
      (metaC: MetaConversation) =>
        metaC.isAssignedToMe &&
        (metaC.conversation.initial_channel === 'pstnphone' ||
          metaC.conversation.initial_channel === 'callback') &&
        metaC.conversation.is_closed === false &&
        metaC.conversation.id !== activeTransferCSID,
    );

    if (foundMetaConversation) {
      const option = {
        title: 'Conversation closed',
        text: 'You ended the conversation on a Jabra device.',
        className: 'info',
        showDesktop: true,
        icon: 'headset',
        time: 5000,
        mainAction: {
          navigate: '/dashboard',
        },
      };
      yield put(notificationAdd(option));
      yield put(
        conversationClose({ csid: foundMetaConversation.conversation.id }),
      );
    }
  } else {
    yield put(jabraButtonClickedUnsupported(action.payload));
  }
}

function* jabraButtonClickedAcceptedCallOfferConversationGen(action) {
  const csid = action.payload.csid;
  const isInternal = action.payload.isInternal;
  const channel = action.payload.channel;
  const isEndUserConnected =
    action.payload.end_user_connected === false ? false : true;
  const transferType = action.payload.transferType
    ? action.payload.transferType
    : null;

  const option = {
    id: uuidv4(),
    title: 'Conversation accepted',
    text: 'You accepted a conversation on a Jabra device.',
    className: 'success',
    showDesktop: false,
    icon: 'headset',
    time: 5000,
  };
  yield put(notificationAdd(option));
  yield put(notificationRemoveByType({ type: 'conversationOffer_' + csid })); // Remove any offer notification.

  if (transferType) {
    yield put(
      conversationRespondeToTransferOfferAccepted({
        csid: csid,
        did_accept: true,
        isInternal: isInternal,
        channel: channel,
      }),
    );
  } else {
    yield put(
      conversationRespondeToOfferAccepted({
        csid: csid,
        did_accept: true,
        isInternal: isInternal,
        isEndUserConnected: isEndUserConnected,
        channel: channel,
      }),
    );
  }
}

function* jabraButtonClickedRejectedCallOfferConversationGen(action) {
  const csid = action.payload.csid;
  const isInternal = action.payload.isInternal;
  const isEndUserConnected =
    action.payload.end_user_connected === false ? false : true;
  const transferType = action.payload.transferType
    ? action.payload.transferType
    : null;

  const option = {
    id: uuidv4(),
    title: 'Rejected call',
    text: 'You rejected a call on Jabra device',
    className: 'info',
    showDesktop: false,
    icon: 'headset',
    time: 5000,
  };

  yield put(notificationAdd(option));
  yield put(notificationRemoveByType({ type: 'conversationOffer_' + csid })); // Remove any offer notification.

  if (transferType) {
    yield put(
      conversationRespondeToTransferOfferRejected({
        csid: csid,
        did_accept: false,
        isInternal: isInternal,
      }),
    );
  } else {
    yield put(
      conversationRespondeToOfferRejected({
        csid: csid,
        did_accept: false,
        isInternal: isInternal,
        isEndUserConnected: isEndUserConnected,
      }),
    );
  }
}
